// RunDemandModal — Reforma UX §6.8 / wireframe dlgRunDemand (PIPE-06).
//
// "Rodar demanda novamente": reenvia uma demanda BLOQUEADA pro pipeline —
// cria uma NOVA pipeline run via POST /v1/pipelines/:id/start (run.start),
// caminho real exposto pelo client (api-pipelines.startPipeline).
//
// AMBIGUIDADE INVESTIGADA (reportada):
// - O wireframe oferece "Etapa de retorno" (retomar da etapa bloqueada /
//   reiniciar do começo / etapa específica). O endpoint /start NÃO aceita
//   etapa de retorno — só demand_id + inputs. Logo só "Reiniciar do começo"
//   é executável hoje; as outras opções aparecem mas disparam aviso graceful.
// - "Informações adicionais" não tem campo no contrato do /start (inputs são
//   validados contra o schema do pipeline) — exibido, mas NÃO transmitido
//   (anotado na UI). Alternativa via PATCH /v1/demands/:id/transition não
//   re-cria run — startPipeline é o caminho que bate com a copy do wireframe
//   ("uma nova pipeline run será criada").

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Play } from 'lucide-react';
import { startPipeline } from '@/lib/api-pipelines';
import type { Demand } from '@/lib/api';

const RETURN_STEP_OPTIONS = [
  { value: 'restart', label: 'Reiniciar do começo' },
  { value: 'resume', label: 'Retomar da etapa bloqueada (indisponível)' },
  { value: 'specific', label: 'Etapa específica… (indisponível)' },
];

export interface RunDemandModalProps {
  open: boolean;
  onClose: () => void;
  pipelineId: string;
  demand: Demand | null;
  /** Toast de sucesso na page/modal pai (run id criada). */
  onStarted: (msg: string) => void;
}

/** Form interno — desmonta junto com o DialogContent (estado zera por abertura). */
function RunDemandForm({
  pipelineId,
  demand,
  onClose,
  onStarted,
}: {
  pipelineId: string;
  demand: Demand;
  onClose: () => void;
  onStarted: (msg: string) => void;
}) {
  const [returnStep, setReturnStep] = useState('restart');
  const [extraInfo, setExtraInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (returnStep !== 'restart') {
      // Graceful: backend run.start não suporta retomar de etapa específica.
      setError(
        'Retomar de etapa específica ainda não é suportado pelo backend '
        + '(POST /v1/pipelines/:id/start sempre inicia nova run do começo). '
        + 'Selecione "Reiniciar do começo".',
      );
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await startPipeline(pipelineId, demand.id);
      onStarted(
        `Demanda ${demand.code ?? demand.id.slice(0, 8)} reenviada — nova run `
        + `${res.pipeline_run_id.slice(0, 8)}… criada.`,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar a run.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <p className="text-sm text-raios-text-secondary leading-relaxed">
          Reenviar{' '}
          <b className="font-mono text-raios-text">{demand.code ?? demand.id.slice(0, 8)}</b>{' '}
          para o pipeline? Uma nova <strong>pipeline run</strong> será criada e a demanda
          volta a ser processada.
        </p>
        <Select
          label="Etapa de retorno"
          options={RETURN_STEP_OPTIONS}
          value={returnStep}
          onChange={setReturnStep}
        />
        <div className="space-y-1.5">
          <Label htmlFor="rd-extra">Informações adicionais (opcional)</Label>
          <Textarea
            id="rd-extra"
            value={extraInfo}
            onChange={(e) => setExtraInfo(e.target.value)}
            rows={3}
            placeholder="Contexto extra para o worker resolver o que bloqueou."
          />
          <p className="text-[11px] text-raios-muted">
            Ainda não transmitido ao backend — o contrato de POST /v1/pipelines/:id/start
            não tem campo de contexto extra (gap reportado).
          </p>
        </div>
        {error && <p className="text-sm text-raios-danger-text">{error}</p>}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="brand" onClick={handleSubmit} loading={submitting}>
          <Play className="h-4 w-4" />
          Rodar demanda
        </Button>
      </DialogFooter>
    </>
  );
}

export function RunDemandModal({ open, onClose, pipelineId, demand, onStarted }: RunDemandModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[450]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-4 w-4 text-raios-accent-text" />
            Rodar demanda novamente
          </DialogTitle>
        </DialogHeader>
        {open && demand && (
          <RunDemandForm
            pipelineId={pipelineId}
            demand={demand}
            onClose={onClose}
            onStarted={onStarted}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
